import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';

export interface ReaderDisplaySettings {
  fontSize: number;
  lineHeight: number;
  paragraphSpacing: number;
}

export const DEFAULT_READER_DISPLAY_SETTINGS: ReaderDisplaySettings = {
  fontSize: 15,
  lineHeight: 1.75,
  paragraphSpacing: 10,
};

const STORAGE_PREFIX = 'reader_display_settings_v1:';

const clampSettings = (input: ReaderDisplaySettings): ReaderDisplaySettings => ({
  fontSize: Math.min(22, Math.max(13, Math.round(input.fontSize))),
  lineHeight: Math.min(2.4, Math.max(1.3, Number(input.lineHeight.toFixed(2)))),
  paragraphSpacing: Math.min(24, Math.max(4, Math.round(input.paragraphSpacing))),
});

const resolveBaseDefaults = (defaults?: Partial<ReaderDisplaySettings>): ReaderDisplaySettings => (
  clampSettings({
    ...DEFAULT_READER_DISPLAY_SETTINGS,
    ...(defaults || {}),
  })
);

const getStorageKey = (scope: string): string => `${STORAGE_PREFIX}${String(scope || 'global').trim() || 'global'}`;

const loadSettings = (scope: string, defaults?: Partial<ReaderDisplaySettings>): ReaderDisplaySettings => {
  const base = resolveBaseDefaults(defaults);
  if (typeof window === 'undefined') return base;
  try {
    const raw = localStorage.getItem(getStorageKey(scope));
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<ReaderDisplaySettings> | null;
    if (!parsed) return base;
    return clampSettings({
      fontSize: Number(parsed.fontSize ?? base.fontSize),
      lineHeight: Number(parsed.lineHeight ?? base.lineHeight),
      paragraphSpacing: Number(parsed.paragraphSpacing ?? base.paragraphSpacing),
    });
  } catch {
    return base;
  }
};

const saveSettings = (scope: string, settings: ReaderDisplaySettings): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(getStorageKey(scope), JSON.stringify(clampSettings(settings)));
  } catch {
    // noop
  }
};

export const useReaderDisplaySettings = (scope: string, defaults?: Partial<ReaderDisplaySettings>) => {
  const [settings, setSettings] = useState<ReaderDisplaySettings>(() => loadSettings(scope, defaults));
  useEffect(() => {
    saveSettings(scope, settings);
  }, [scope, settings]);

  const update = (next: Partial<ReaderDisplaySettings>) => {
    setSettings((prev) => clampSettings({ ...prev, ...next }));
  };

  const reset = () => {
    setSettings(resolveBaseDefaults(defaults));
  };

  return { settings, update, reset } as const;
};

interface ReaderSettingsMenuProps {
  settings: ReaderDisplaySettings;
  onChange: (next: Partial<ReaderDisplaySettings>) => void;
  onReset: () => void;
  buttonClassName?: string;
}

export const ReaderSettingsMenu: React.FC<ReaderSettingsMenuProps> = ({
  settings,
  onChange,
  onReset,
  buttonClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node | null;
      if (target && rootRef.current.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const panel = useMemo(() => (
    <div className="absolute right-0 top-full z-50 mt-2 w-[272px] rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
      <p className="text-sm font-semibold text-slate-800">阅读排版</p>
      <div className="mt-3 space-y-3">
        <label className="block">
          <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
            <span>字号</span>
            <span>{settings.fontSize}px</span>
          </div>
          <input
            type="range"
            min={13}
            max={22}
            step={1}
            value={settings.fontSize}
            onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
            className="w-full"
          />
        </label>
        <label className="block">
          <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
            <span>行间距</span>
            <span>{settings.lineHeight.toFixed(2)}</span>
          </div>
          <input
            type="range"
            min={1.3}
            max={2.4}
            step={0.05}
            value={settings.lineHeight}
            onChange={(e) => onChange({ lineHeight: Number(e.target.value) })}
            className="w-full"
          />
        </label>
        <label className="block">
          <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
            <span>段间距</span>
            <span>{settings.paragraphSpacing}px</span>
          </div>
          <input
            type="range"
            min={4}
            max={24}
            step={1}
            value={settings.paragraphSpacing}
            onChange={(e) => onChange({ paragraphSpacing: Number(e.target.value) })}
            className="w-full"
          />
        </label>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          onClick={onReset}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          恢复默认
        </button>
      </div>
    </div>
  ), [onChange, onReset, settings.fontSize, settings.lineHeight, settings.paragraphSpacing]);

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/20 ${buttonClassName}`}
        aria-label="阅读设置"
      >
        <SlidersHorizontal className="h-4 w-4" />
        <span className="hidden sm:inline">设置</span>
      </button>
      {open && panel}
    </div>
  );
};
